/**
 * Engagement by Activities — combined line chart.
 * One line per activity kind plotting distinct users per day (or per week
 * when bucketed). Replaces the earlier small-multiples grid so all five
 * activities can be compared at a glance on a shared axis.
 *
 * Used by Dashboard Usage tab and Teacher Stats page.
 */

import { useMemo, useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import type { ActivityEngagementSeries, EngagementActivityKind } from '../../types';
import './EngagementByActivitiesChart.css';

/** What each line counts. `users` is the platform view (distinct users per
 *  day). For ONE user that is only ever 0 or 1, so a single-user view plots
 *  `events` — how much they actually did. */
type Metric = 'users' | 'events';

interface Props {
    series: ActivityEngagementSeries[];
    /** Bucket daily points into 7-day weeks. Frontend chooses based on range. */
    weekly: boolean;
    /** Defaults to the platform's distinct-users view. */
    metric?: Metric;
    /** Overrides the section heading — e.g. when scoped to one student. */
    title?: string;
}

const KIND_META: Record<EngagementActivityKind, { label: string; color: string }> = {
    submission:        { label: 'Submissions',        color: '#3b82f6' },
    // A lighter shade of the Submissions blue, because it IS Submissions —
    // a distinct hue would read as an unrelated activity.
    practice_submission: { label: 'Practice Submissions', color: '#7dd3fc' },
    hint_request:      { label: 'Hint Requests',      color: '#8b5cf6' },
    concept_lab_open:  { label: 'Concept Lab',        color: '#10b981' },
    text_chat_start:   { label: 'Text Chat',          color: '#f59e0b' },
    tag_action:        { label: 'Tag Actions',        color: '#ef4444' },
    solution_analysis_view: { label: 'Solution Views', color: '#ec4899' },
    // Two shades of one hue: a lab session and the mixes inside it are the same
    // activity at two grains, and separate hues would read as unrelated.
    chemistry_lab_session: { label: 'Lab Sessions',     color: '#0d9488' },
    chemistry_lab_mix:     { label: 'Lab Mixes',        color: '#5eead4' },
    simulation_open:       { label: 'Simulations',      color: '#a78bfa' },
};

const TOOLTIP_CONTENT_STYLE = {
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--text-primary)' };
const TOOLTIP_ITEM_STYLE = { color: 'var(--text-primary)' };

function formatDay(date: string): string {
    const d = new Date(date + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatWeekRange(date: string): string {
    const start = new Date(date + 'T00:00:00Z');
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    return `${s} – ${e}`;
}

/**
 * One row per date with a column per activity kind. When `weekly` is true,
 * daily values get summed into 7-day buckets keyed by the chunk's first day
 * (`users` becomes a user-day total — a user active 3 days in the week is
 * counted 3×, which the chart's tooltip notes via the "User-days" label).
 */
type CombinedRow = { date: string } & Partial<Record<EngagementActivityKind, number>>;

function buildCombinedRows(series: ActivityEngagementSeries[], weekly: boolean, metric: Metric): CombinedRow[] {
    // Collect every date that any series has data for.
    const dateSet = new Set<string>();
    for (const s of series) for (const p of s.daily) dateSet.add(p.date);
    const dates = Array.from(dateSet).sort();
    if (dates.length === 0) return [];

    // Index per series for O(1) lookup while we walk the sorted dates.
    const lookup = new Map<EngagementActivityKind, Map<string, number>>();
    for (const s of series) {
        const m = new Map<string, number>();
        for (const p of s.daily) m.set(p.date, metric === 'events' ? p.events : p.users);
        lookup.set(s.kind, m);
    }

    if (!weekly) {
        return dates.map(date => {
            const row: CombinedRow = { date };
            for (const [kind, m] of lookup) row[kind] = m.get(date) ?? 0;
            return row;
        });
    }

    // Weekly: bucket sorted dates into 7-day chunks; the bucket key is the
    // first date in the chunk (matches the prior small-multiples behavior).
    const buckets: CombinedRow[] = [];
    for (let i = 0; i < dates.length; i += 7) {
        const chunk = dates.slice(i, i + 7);
        const row: CombinedRow = { date: chunk[0] };
        for (const [kind, m] of lookup) {
            row[kind] = chunk.reduce((sum, d) => sum + (m.get(d) ?? 0), 0);
        }
        buckets.push(row);
    }
    return buckets;
}

export function EngagementByActivitiesChart({ series, weekly, metric = 'users', title }: Props) {
    const rows = useMemo(() => buildCombinedRows(series, weekly, metric), [series, weekly, metric]);
    // null = no selection (all lines shown at full prominence). When set,
    // only the selected line is fully drawn; the others fade so the focus
    // is unambiguous. Click the same legend entry again to clear.
    const [selected, setSelected] = useState<EngagementActivityKind | null>(null);

    const totalEvents = useMemo(
        () => series.reduce((s, x) => s + x.daily.reduce((y, p) => y + p.events, 0), 0),
        [series],
    );

    if (totalEvents === 0) {
        return (
            <section className="eba-section">
                <h3>Engagement by Activities</h3>
                <p className="eba-section-sub">No activity events in this range.</p>
            </section>
        );
    }

    // Render lines in the order the backend gave us, which keeps colors
    // stable per activity kind across renders. A kind we have no meta for is
    // dropped rather than drawn: the backend owns this list, so it can add one
    // ahead of the console, and an unmapped kind used to take the whole tab
    // down on `KIND_META[kind].label`.
    const kinds = series.map(s => s.kind).filter(kind => kind in KIND_META);
    const unitLabel = metric === 'events'
        ? 'actions'
        : (weekly ? 'user-days' : 'distinct users');

    const handleLegendClick = (entry: { dataKey?: string | number | ((obj: unknown) => unknown) }) => {
        const k = typeof entry.dataKey === 'string' ? entry.dataKey as EngagementActivityKind : undefined;
        if (!k || !(k in KIND_META)) return;
        setSelected(prev => (prev === k ? null : k));
    };

    return (
        <section className="eba-section">
            <h3>{title ?? 'Engagement by Activities'}</h3>
            <p className="eba-section-sub">
                {unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1)} per
                {weekly ? ' week' : ' day'}, one line per activity.
                {' '}Click an activity in the legend to highlight it.
            </p>
            <div className="eba-card">
                <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v) => formatDay(String(v))}
                            tick={{ fontSize: 11 }}
                            stroke="var(--text-tertiary, #9ca3af)"
                            interval="preserveStartEnd"
                            minTickGap={20}
                        />
                        <YAxis
                            tick={{ fontSize: 11 }}
                            stroke="var(--text-tertiary, #9ca3af)"
                            allowDecimals={false}
                        />
                        <Tooltip
                            labelFormatter={(label) => weekly ? formatWeekRange(String(label)) : formatDay(String(label))}
                            formatter={(value, name) => {
                                if (value === null || value === undefined) return ['—', name];
                                return [value, name];
                            }}
                            contentStyle={TOOLTIP_CONTENT_STYLE}
                            labelStyle={TOOLTIP_LABEL_STYLE}
                            itemStyle={TOOLTIP_ITEM_STYLE}
                        />
                        <Legend
                            wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
                            onClick={handleLegendClick}
                        />
                        {kinds.map(kind => {
                            const isHighlighted = selected === null || selected === kind;
                            return (
                                <Line
                                    key={kind}
                                    type="monotone"
                                    dataKey={kind}
                                    name={KIND_META[kind].label}
                                    stroke={KIND_META[kind].color}
                                    strokeWidth={isHighlighted ? (selected === kind ? 3 : 2) : 1}
                                    strokeOpacity={isHighlighted ? 1 : 0.25}
                                    dot={{ r: isHighlighted ? 2 : 1.5, opacity: isHighlighted ? 1 : 0.3 }}
                                    activeDot={{ r: 4 }}
                                    connectNulls
                                />
                            );
                        })}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </section>
    );
}
